import { type KeyboardEvent, type PointerEvent as ReactPointerEvent, useId, useRef } from 'react'
import * as m from '~/paraglide/messages'

interface FlyerCropFieldProps {
  src: string
  aspectRatio: number
  focalX: number
  focalY: number
  onChange: (focal: { x: number; y: number }) => void
}

const KEY_STEP = 0.02

function clamp(value: number): number {
  return Math.min(1, Math.max(0, value))
}

/**
 * CSS `object-position` offsets a cover-fitted image by the same fraction of the
 * overflow that the renderer extracts, so what is framed here is what prints.
 */
export function FlyerCropField({
  src,
  aspectRatio,
  focalX,
  focalY,
  onChange,
}: FlyerCropFieldProps) {
  const hintId = useId()
  const boxRef = useRef<HTMLButtonElement>(null)
  const dragging = useRef<{ x: number; y: number; focalX: number; focalY: number } | null>(null)

  function handlePointerDown(event: ReactPointerEvent<HTMLButtonElement>) {
    event.currentTarget.setPointerCapture(event.pointerId)
    dragging.current = { x: event.clientX, y: event.clientY, focalX, focalY }
  }

  function handlePointerMove(event: ReactPointerEvent<HTMLButtonElement>) {
    const start = dragging.current
    const box = boxRef.current
    if (!start || !box) return
    const rect = box.getBoundingClientRect()
    if (rect.width === 0 || rect.height === 0) return
    // Dragging right reveals more of the left edge, hence the inverted delta.
    onChange({
      x: clamp(start.focalX - (event.clientX - start.x) / rect.width),
      y: clamp(start.focalY - (event.clientY - start.y) / rect.height),
    })
  }

  function handlePointerUp(event: ReactPointerEvent<HTMLButtonElement>) {
    event.currentTarget.releasePointerCapture(event.pointerId)
    dragging.current = null
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    const step = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[
      event.key
    ]
    if (!step) return
    event.preventDefault()
    onChange({
      x: clamp(focalX + step[0] * KEY_STEP),
      y: clamp(focalY + step[1] * KEY_STEP),
    })
  }

  return (
    <div className="space-y-1.5">
      <button
        ref={boxRef}
        type="button"
        aria-label={m.flyerCropLabel()}
        aria-describedby={hintId}
        // Width-capped so a wide band cannot push the dialog into horizontal
        // scroll; the aspect ratio then drives the height on narrow screens.
        style={{
          aspectRatio: `${aspectRatio}`,
          width: `min(100%, calc(min(14rem, 30vh) * ${aspectRatio}))`,
        }}
        className="block cursor-move touch-none overflow-hidden rounded-md border bg-muted outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onKeyDown={handleKeyDown}
      >
        <img
          src={src}
          alt=""
          draggable={false}
          className="h-full w-full object-cover"
          style={{ objectPosition: `${focalX * 100}% ${focalY * 100}%` }}
        />
      </button>
      <p id={hintId} className="max-w-xs text-xs text-muted-foreground">
        {m.flyerCropHint()}
      </p>
    </div>
  )
}
